import { Link, Outlet } from "react-router-dom";
import { useState } from "react";
import { Store as StoreIcon } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { MobileTabBar } from "./MobileTabBar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useAppFilters } from "@/state/AppFilters";

export function BetaLaunchNotice() {
  return (
    <div
      className="border-b border-border/60 bg-card/60 px-3 py-2 text-xs text-muted-foreground md:px-8"
      role="status"
      aria-label="StoreLedger Pro beta notice"
    >
      <div className="mx-auto flex w-full max-w-[1600px] flex-wrap items-center gap-2">
        <Badge variant="outline" className="border-warning/40 text-warning">
          Beta
        </Badge>
        <span>
          StoreLedger Pro is in beta. Passport import is read-only, and invoice OCR/PDF import is beta,
          draft-only, and requires manual review.
        </span>
      </div>
    </div>
  );
}

function FirstStoreSetupNotice() {
  const { canCreateFirstStore } = useAuth();
  const { stores, storesLoading } = useAppFilters();

  if (!canCreateFirstStore || storesLoading || stores.length > 0) return null;

  return (
    <div className="border-b border-primary/30 bg-primary/10 px-3 py-3 text-sm md:px-8" role="status">
      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="font-medium text-foreground">Create your first store to finish setup</div>
          <p className="text-xs text-muted-foreground">
            New business owners can create a store first, then connect Gilbarco Passport from Integration Hub.
          </p>
        </div>
        <Button asChild size="sm" className="shrink-0">
          <Link to="/settings?tab=store">Create your first store</Link>
        </Button>
      </div>
    </div>
  );
}

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="flex min-h-dvh w-full bg-background">
      {/* Desktop / tablet sidebar — hidden on phones (replaced by bottom tab bar) */}
      <div className="hidden md:flex">
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
      </div>

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Compact mobile top bar — branding only, no navigation drawer */}
        <header className="md:hidden sticky top-0 z-30 flex items-center gap-2 h-12 px-3 border-b border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70 pt-safe">
          <div className="h-7 w-7 rounded-md bg-gradient-primary flex items-center justify-center shadow-glow shrink-0">
            <StoreIcon className="h-3.5 w-3.5 text-primary-foreground" />
          </div>
          <div className="leading-tight min-w-0">
            <div className="text-sm font-semibold text-foreground truncate">
              StoreLedger
            </div>
            <div className="text-[10px] text-muted-foreground tracking-wider uppercase">
              Pro
            </div>
          </div>
        </header>

        <BetaLaunchNotice />
        <FirstStoreSetupNotice />

        <div className="px-3 sm:px-4 md:px-8 py-4 md:py-8 w-full min-w-0 max-w-[1600px] mx-auto animate-fade-in pb-tabbar">
          <Outlet />
        </div>
      </main>

      <MobileTabBar />
    </div>
  );
}
